import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { FaArrowUp } from 'react-icons/fa';

interface WalletTransaction {
  id: string;
  tipo: string;
  monto: number;
  fecha: string | Date;
  estado: string;
  taskTitle?: string;
  startDate?: any;
  duration?: number;
  fee?: number;
  feePercent?: number;
  total?: number;
  projectId?: string;
  assignmentRequestId?: string;
  businessId?: string;
}

const Balance: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const [walletTransactions, setWalletTransactions] = useState<WalletTransaction[]>([]);
  const [walletTab, setWalletTab] = useState('all');
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const TABS = [
    { key: 'all', label: t('balance.allTab', 'Todas') },
    { key: 'pendiente', label: t('balance.pendingTab', 'Pendientes') },
    { key: 'pagado', label: t('balance.paidTab', 'Pagadas') },
    { key: 'retiro', label: t('balance.withdrawalTab', 'Retiros') },
  ];

  useEffect(() => {
    const fetchWalletTransactions = async () => {
      try {
        if (!currentUser) return;
        const db = getFirestore();
        // Leer balance principal
        const walletDocRef = doc(db, 'wallet', currentUser.uid);
        const walletDoc = await getDoc(walletDocRef);
        if (walletDoc.exists() && walletDoc.data().disponible !== undefined) {
          setBalance(walletDoc.data().disponible + (walletDoc.data().pendiente || 0));
        } else {
          setBalance(0);
        }
        // Leer transacciones
        const txCol = collection(db, 'wallet', currentUser.uid, 'transacciones');
        const txSnap = await getDocs(txCol);
        const txs: WalletTransaction[] = txSnap.docs.map(docSnap => {
          const d = docSnap.data();
          return {
            id: docSnap.id,
            tipo: d.tipo,
            monto: d.monto,
            fecha: d.fecha instanceof Date ? d.fecha : (d.fecha?.toDate ? d.fecha.toDate() : new Date(d.fecha)),
            estado: d.estado,
            taskTitle: d.taskTitle,
            startDate: d.startDate instanceof Date ? d.startDate : (d.startDate?.toDate ? d.startDate.toDate() : d.startDate ? new Date(d.startDate) : null),
            duration: d.duration,
            fee: d.fee,
            feePercent: d.feePercent,
            total: d.total,
            projectId: d.projectId,
            assignmentRequestId: d.assignmentRequestId,
            businessId: d.businessId,
          };
        });
        setWalletTransactions(txs);
      } catch (error) {
        setError(t('balance.errorLoading'));
      } finally {
        setLoading(false);
      }
    };
    fetchWalletTransactions();
  }, [currentUser, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-6">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-6">
        <div className="flex items-center justify-center min-h-screen">
          <div className="bg-red-50 border border-red-200 rounded-3xl p-8 max-w-md shadow-2xl">
            <p className="text-red-700 text-center">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100 p-8 font-sans">
      {/* Toast de alerta */}
      {/* Eliminar el hook de toast si no se usa */}
      {/* {toast && ( */}
      {/*   <div className={`fixed top-6 right-6 z-[9999] px-6 py-4 rounded-xl shadow-lg text-white text-lg font-semibold transition-all duration-500 animate-fadeIn ${toast.type === 'success' ? 'bg-red-600' : 'bg-black'}`}> */}
      {/*     {toast.message} */}
      {/*   </div> */}
      {/* )} */}
      <div className="max-w-4xl mx-auto">
        {/* Tabs de transacciones wallet */}
        <div className="flex gap-2 mb-8">
          {TABS.map(tabObj => (
            <button
              key={tabObj.key}
              onClick={() => setWalletTab(tabObj.key)}
              className={`px-6 py-2 rounded-t-2xl font-bold text-lg transition-all border-b-4 ${walletTab===tabObj.key ? 'bg-white border-red-500 text-red-600 shadow' : 'bg-red-100 border-transparent text-gray-500'}`}
            >
              {tabObj.label}
            </button>
          ))}
        </div>

        {/* Filtro de fechas */}
        <div className="flex flex-col md:flex-row gap-4 items-center mb-8 bg-white rounded-2xl p-6 shadow border border-white/40">
          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-semibold mb-1">{t('balance.startDate')}</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-gray-700 font-semibold mb-1">{t('balance.endDate')}</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className="px-4 py-2 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
            />
          </div>
        </div>

        {/* Balance Header */}
        <div className="bg-white rounded-3xl p-10 mb-10 shadow-2xl border border-white/40">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-6 tracking-tight drop-shadow-lg">
            <span className="bg-gradient-to-r from-red-500 via-pink-400 to-red-600 bg-clip-text text-transparent">
              {t('balance.title')}
            </span>
          </h1>
          <div className="text-5xl font-bold text-red-600 mb-4">
            {new Intl.NumberFormat(i18n.language, {
              style: 'currency',
              currency: 'USD'
            }).format(balance)}
          </div>
        </div>

        {/* Lista de transacciones filtradas por tab y fechas */}
        <div className="space-y-6">
          {walletTransactions.filter(tx => {
            // Filtrado por tab
            if (walletTab === 'pendiente' && tx.estado !== 'pendiente') return false;
            if (walletTab === 'pagado' && tx.estado !== 'pagado') return false;
            if (walletTab === 'retiro' && tx.tipo !== 'retiro') return false;
            // Filtrado por fechas
            if (startDate && new Date(tx.fecha) < new Date(startDate)) return false;
            if (endDate && new Date(tx.fecha) > new Date(endDate + 'T23:59:59')) return false;
            return true;
          }).length > 0 ? (
            walletTransactions.filter(tx => {
              if (walletTab === 'pendiente' && tx.estado !== 'pendiente') return false;
              if (walletTab === 'pagado' && tx.estado !== 'pagado') return false;
              if (walletTab === 'retiro' && tx.tipo !== 'retiro') return false;
              if (startDate && new Date(tx.fecha) < new Date(startDate)) return false;
              if (endDate && new Date(tx.fecha) > new Date(endDate + 'T23:59:59')) return false;
              return true;
            }).map((tx) => (
              <div key={tx.id} className="bg-white rounded-3xl p-8 shadow-2xl border border-white/40 hover:shadow-3xl transition-all duration-200 hover:transform hover:-translate-y-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                  <div>
                    <div className="font-semibold text-gray-900 text-lg mb-1">{tx.taskTitle || t('balance.noTaskTitle')}</div>
                    <div className="text-sm text-gray-600 mb-1">{t('balance.type')}: {tx.tipo}</div>
                    <div className="text-sm text-gray-600 mb-1">{t('balance.status')}: {tx.estado}</div>
                    <div className="text-sm text-gray-600 mb-1">{t('balance.createdAt')}: {format(new Date(tx.fecha), 'PPP', { locale: i18n.language === 'es' ? es : enUS })}</div>
                    {tx.startDate && (
                      <div className="text-sm text-gray-600 mb-1">{t('balance.startDate')}: {format(new Date(tx.startDate), 'PPP', { locale: i18n.language === 'es' ? es : enUS })}</div>
                    )}
                    {tx.duration && (
                      <div className="text-sm text-gray-600 mb-1">{t('balance.duration')}: {tx.duration} {t('projects.assignment.' + (tx.duration === 1 ? 'day' : 'days'))}</div>
                    )}
                    {/* Fecha estimada de pago */}
                    {tx.startDate && tx.duration && (
                      <div className="text-sm text-green-700 font-semibold mb-1">{t('balance.estimatedPayment')}: {format(new Date(new Date(tx.startDate).getTime() + tx.duration * 24 * 60 * 60 * 1000), 'PPP', { locale: i18n.language === 'es' ? es : enUS })}</div>
                    )}
                    {tx.fee !== undefined && (
                      <div className="text-xs text-gray-400">{t('balance.fee')}: {tx.fee} ({tx.feePercent}%)</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    <div className={`font-bold text-2xl ${tx.tipo === 'retiro' ? 'text-red-600' : 'text-green-600'}`}>{tx.tipo === 'retiro' ? '-' : '+'}{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(tx.monto)}</div>
                    {tx.total !== undefined && tx.tipo === 'pendiente_pago' && (
                      <div className="text-xs text-gray-500">{t('balance.grossTotal')}: {new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'USD' }).format(tx.total)}</div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-3xl p-16 text-center shadow-2xl border border-white/40">
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FaArrowUp className="text-3xl text-gray-400" />
                </div>
                <div className="text-gray-400 text-lg font-semibold">{t('balance.noTransactions')}</div>
              </div>
            </div>
          )}
        </div>

        {/* Tab de métodos de pago (sin cambios) */}
        {/* Eliminar el hook de tab y setTab si no se usa */}
        {/* const [tab, setTab] = useState<'balance'|'payment'>('balance'); */}
        {/* Eliminar el código de la pestaña de pago si no se usa */}
        {/* {tab === 'payment' && ( */}
        {/*   <div className="bg-white rounded-3xl p-10 shadow-2xl border border-white/40"> */}
        {/*     <h2 className="text-2xl font-bold mb-6 text-red-600">{t('balance.paymentTab')}</h2> */}
        {/*     <div className="mb-6 flex flex-wrap gap-4"> */}
        {/*       {Object.entries(paymentMethodLabels).map(([key, label]) => ( */}
        {/*         <button */}
        {/*           key={key} */}
        {/*           onClick={()=>setSelectedMethod(key as PaymentMethod)} */}
        {/*           className={`px-4 py-2 rounded-xl font-semibold border-2 transition-all ${selectedMethod===key ? 'bg-red-100 border-red-500 text-red-600' : 'bg-gray-100 border-gray-300 text-gray-700'}`} */}
        {/*         > */}
        {/*           {label} */}
        {/*         </button> */}
        {/*       ))} */}
        {/*     </div> */}
        {/*     {/* Formulario dinámico según método */}
        {/*     <div className="mb-6"> */}
        {/*       {selectedMethod === 'cash' && ( */}
        {/*         <div className="flex items-center gap-4"> */}
        {/*           <span className="text-gray-700">{t('balance.allowCash')}</span> */}
        {/*           <button */}
        {/*             type="button" */}
        {/*             className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${paymentPrefs.cash === true ? 'bg-red-600' : 'bg-gray-300'}`} */}
        {/*             onClick={async () => { */}
        {/*               if (!currentUser) return; */}
        {/*               setSaving(true); */}
        {/*               try { */}
        {/*                 const db = getFirestore(); */}
        {/*                 const userRef = doc(db, 'users', currentUser.uid); */}
        {/*                 const updatedPrefs = { */}
        {/*                   ...paymentPrefs, */}
        {/*                   cash: paymentPrefs.cash === true ? false : true, */}
        {/*                 }; */}
        {/*                 await updateDoc(userRef, { paymentPrefs: updatedPrefs }); */}
        {/*                 setPaymentPrefs(updatedPrefs); */}
        {/*                 setToast({ message: 'Guardado', type: 'success' }); */}
        {/*               } catch (e) { */}
        {/*                 setToast({ message: 'Error al guardar', type: 'error' }); */}
        {/*               } finally { */}
        {/*                 setSaving(false); */}
        {/*               } */}
        {/*             }} */}
        {/*           > */}
        {/*             <span */}
        {/*               className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${paymentPrefs.cash === true ? 'translate-x-6' : 'translate-x-1'}`} */}
        {/*             /> */}
        {/*           </button> */}
        {/*           <span className="text-sm text-gray-500">{paymentPrefs.cash === true ? t('balance.enabled') : t('balance.disabled')}</span> */}
        {/*         </div> */}
        {/*       )} */}
        {/*       {selectedMethod === 'check' && ( */}
        {/*         <input */}
        {/*           type="text" */}
        {/*           placeholder={t('balance.checkName')} */}
        {/*           value={newPaymentData.name || ''} */}
        {/*           onChange={e=>setNewPaymentData({...newPaymentData, name: e.target.value})} */}
        {/*           className="mb-2 px-4 py-2 border rounded-xl w-full" */}
        {/*         /> */}
        {/*       )} */}
        {/*       {selectedMethod === 'paypal' && ( */}
        {/*         <input */}
        {/*           type="email" */}
        {/*           placeholder={t('balance.paypalEmail')} */}
        {/*           value={newPaymentData.email || ''} */}
        {/*           onChange={e=>setNewPaymentData({...newPaymentData, email: e.target.value})} */}
        {/*           className="mb-2 px-4 py-2 border rounded-xl w-full" */}
        {/*         /> */}
        {/*       )} */}
        {/*       {selectedMethod === 'bank_transfer' && ( */}
        {/*         <> */}
        {/*           <input */}
        {/*             type="text" */}
        {/*             placeholder={t('balance.bank')} */}
        {/*             value={newPaymentData.bank || ''} */}
        {/*             onChange={e=>setNewPaymentData({...newPaymentData, bank: e.target.value})} */}
        {/*             className="mb-2 px-4 py-2 border rounded-xl w-full" */}
        {/*           /> */}
        {/*           <input */}
        {/*             type="text" */}
        {/*             placeholder={t('balance.account')} */}
        {/*             value={newPaymentData.account || ''} */}
        {/*             onChange={e=>setNewPaymentData({...newPaymentData, account: e.target.value})} */}
        {/*             className="mb-2 px-4 py-2 border rounded-xl w-full" */}
        {/*           /> */}
        {/*           <input */}
        {/*             type="text" */}
        {/*             placeholder={t('balance.holder')} */}
        {/*             value={newPaymentData.holder || ''} */}
        {/*             onChange={e=>setNewPaymentData({...newPaymentData, holder: e.target.value})} */}
        {/*             className="mb-2 px-4 py-2 border rounded-xl w-full" */}
        {/*           /> */}
        {/*         </> */}
        {/*       )} */}
        {/*       {selectedMethod !== 'cash' && ( */}
        {/*         <button */}
        {/*           onClick={handleAddPaymentData} */}
        {/*           disabled={saving || Object.keys(newPaymentData).length===0} */}
        {/*           className="mt-2 px-6 py-2 bg-red-600 text-white rounded-xl font-bold shadow hover:bg-red-700 disabled:opacity-50" */}
        {/*         > */}
        {/*           {t('balance.addData')} */}
        {/*         </button> */}
        {/*       )} */}
        {/*     </div> */}
        {/*     {/* Lista de datos guardados */}
        {/*     {selectedMethod !== 'cash' && ( */}
        {/*       <> */}
        {/*         <h3 className="font-semibold mb-2">{t('balance.savedDataFor', { method: t('balance.' + selectedMethod) })}</h3> */}
        {/*         <ul className="space-y-2"> */}
        {/*           {(paymentPrefs[selectedMethod] || []).length === 0 && ( */}
        {/*             <li className="text-gray-500">{t('balance.noData')}</li> */}
        {/*           )} */}
        {/*           {(paymentPrefs[selectedMethod] || []).map((item:any, idx:number) => ( */}
        {/*             <li key={idx} className="bg-gray-100 rounded-xl px-4 py-2 flex items-center gap-2"> */}
        {/*               {selectedMethod==='check' && <span>{t('balance.checkName')}: {item.name}</span>} */}
        {/*               {selectedMethod==='paypal' && <span>{t('balance.paypal')}: {item.email}</span>} */}
        {/*               {selectedMethod==='bank_transfer' && <span>{item.bank} - {item.account} ({item.holder})</span>} */}
        {/*             </li> */}
        {/*           ))} */}
        {/*         </ul> */}
        {/*       </> */}
        {/*     )} */}
        {/*   </div> */}
        {/* )} */}
      </div>
    </div>
  );
};

export default Balance; 